import { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { SOURCE_TYPE_LABEL } from "../pages/AdminUploadPage";
import { SOURCE_TYPE_HIERARCHY, type SourceType, type DocumentRecord } from "@shared/types";

// Same duplication rationale as AdminUploadPage.tsx's own copies of these —
// this file isn't bundled for the Functions runtime, so the size ceilings
// can't be imported from validation.ts either.
const CHUNK_SIZE_BYTES = 4 * 1024 * 1024;
const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

function fileToBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1] ?? "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Filename minus its extension, used to pre-fill Title so a quick attach doesn't force typing a title from scratch. */
function titleFromFilename(name: string): string {
  const idx = name.lastIndexOf(".");
  return idx > 0 ? name.slice(0, idx) : name;
}

/**
 * Compact "+" attach control for the Sources tab — a quick alternative to
 * navigating to the full /upload page, styled after Claude's own composer
 * attach button. Supports the same file types as AdminUploadPage.tsx
 * (PDF/DOCX/images) via the same direct-vs-chunked upload split, just
 * inline in a popover instead of a full page.
 */
export function AttachSourceMenu({ projectId, onUploaded }: { projectId: string; onUploaded: () => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [sourceType, setSourceType] = useState<SourceType>("other");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chunkProgress, setChunkProgress] = useState<{ done: number; total: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function reset() {
    setTitle("");
    setSourceType("other");
    setFile(null);
    setError(null);
    setChunkProgress(null);
  }

  function handleFileChange(f: File | null) {
    setFile(f);
    if (f && !title) setTitle(titleFromFilename(f.name));
  }

  async function uploadDirect(f: File): Promise<DocumentRecord> {
    const fileContentBase64 = await fileToBase64(f);
    const data = await api.post<{ document: DocumentRecord }>(`/api/projects/${projectId}/documents`, {
      title,
      sourceType,
      originalFilename: f.name,
      mimeType: f.type,
      fileContentBase64,
    });
    return data.document;
  }

  async function uploadChunked(f: File): Promise<DocumentRecord> {
    const uploadId = crypto.randomUUID();
    const totalChunks = Math.ceil(f.size / CHUNK_SIZE_BYTES);
    setChunkProgress({ done: 0, total: totalChunks });

    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE_BYTES;
      const chunk = f.slice(start, Math.min(start + CHUNK_SIZE_BYTES, f.size));
      const dataBase64 = await fileToBase64(chunk);
      try {
        await api.post(`/api/projects/${projectId}/documents/upload-chunk`, { uploadId, chunkIndex: i, dataBase64 });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed.";
        throw new Error(`Upload failed on piece ${i + 1} of ${totalChunks}: ${message}`);
      }
      setChunkProgress({ done: i + 1, total: totalChunks });
    }

    const data = await api.post<{ document: DocumentRecord }>(`/api/projects/${projectId}/documents/upload-finalize`, {
      uploadId,
      totalChunks,
      title,
      sourceType,
      originalFilename: f.name,
      mimeType: f.type,
    });
    return data.document;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return;
    setError(null);

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`That file is ${(file.size / 1024 / 1024).toFixed(1)}MB — over the ${MAX_UPLOAD_BYTES / 1024 / 1024}MB upload limit.`);
      return;
    }

    setSubmitting(true);
    try {
      await (file.size <= CHUNK_SIZE_BYTES ? uploadDirect(file) : uploadChunked(file));
      setOpen(false);
      reset();
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setSubmitting(false);
      setChunkProgress(null);
    }
  }

  return (
    <div className="attach-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-primary attach-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Add a source"
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
        <span>Add source</span>
      </button>
      {open && (
        <div className="attach-menu" role="dialog" aria-label="Add a source">
          <p className="muted attach-menu-hint">
            PDF, DOCX, or a photo (PNG/JPEG/WEBP) — a photo of a spec page or a field photo both work.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="field">
              <label htmlFor="attach-file">File</label>
              <input
                id="attach-file"
                type="file"
                accept=".pdf,.docx,.png,.jpg,.jpeg,.webp"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="attach-title">Title</label>
              <input id="attach-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="attach-sourceType">Source type</label>
              <select id="attach-sourceType" value={sourceType} onChange={(e) => setSourceType(e.target.value as SourceType)}>
                {SOURCE_TYPE_HIERARCHY.filter((t) => t !== "veteran_knowledge").map((t) => (
                  <option key={t} value={t}>
                    {SOURCE_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={submitting || !file || !title} style={{ width: "100%" }}>
              {chunkProgress ? `Uploading… (${chunkProgress.done}/${chunkProgress.total})` : submitting ? "Uploading…" : "Upload"}
            </button>
            {error && <p className="error-text" style={{ marginTop: "0.5rem" }}>{error}</p>}
          </form>
        </div>
      )}
    </div>
  );
}
